import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CircleCheckBig, Clock, Trophy } from 'lucide-react';
import { courses, getCourse, STEP_COUNT } from '../content';
import { useProgress } from '../hooks/useProgress';
import Icon from '../components/Icon';
import NotFound from './NotFound';

export default function CoursePage() {
  const { courseId } = useParams();
  const course = getCourse(courseId);
  const { isDone, courseDone, quizScore } = useProgress();
  if (!course) return <NotFound />;

  const idx = courses.indexOf(course);
  const prev = courses[idx - 1];
  const next = courses[idx + 1];
  const done = courseDone(course);
  const pct = done / course.lessons.length;
  const mins = course.lessons.reduce((s, l) => s + l.minutes, 0);
  const firstOpen = course.lessons.find((l) => !isDone(course.id, l.id)) || course.lessons[0];
  const q = quizScore(course.id);

  return (
    <div className="page course-page" style={{ '--c1': course.colors[0], '--c2': course.colors[1] }}>
      <motion.header className="course-hero" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="course-hero-art" aria-hidden="true">
          <motion.span animate={{ rotate: 360 }} transition={{ duration: 40, repeat: Infinity, ease: 'linear' }} className="orbit" />
          <span className="course-hero-icon"><Icon name={course.icon} size={54} /></span>
        </div>
        <div className="course-hero-copy">
          <p className="eyebrow">Step {course.step} of {STEP_COUNT} · {course.level}</p>
          <h1>{course.title}</h1>
          <p className="lede">{course.tagline}</p>
          <div className="course-hero-meta">
            <span><Clock size={16} /> {mins} min</span>
            <span><CircleCheckBig size={16} /> {done} of {course.lessons.length} done</span>
            <span><Trophy size={16} /> {q !== undefined ? `Quiz best ${q}/${course.quiz.length}` : 'Quiz not taken'}</span>
          </div>
          <div className="course-hero-cta">
            <Link className="btn btn-primary btn-lg" to={`/course/${course.id}/${firstOpen.id}`}>
              {done === 0 ? 'Start the course' : done === course.lessons.length ? 'Review lessons' : 'Continue'} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
        <div className="ring-big" aria-label={`${Math.round(pct * 100)}% complete`}>
          <svg viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="52" className="ring-bg" />
            <motion.circle
              cx="60" cy="60" r="52" className="ring-course"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: pct || 0.0001 }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </svg>
          <span>{Math.round(pct * 100)}<small>%</small></span>
        </div>
      </motion.header>

      <ol className="lesson-list">
        {course.lessons.map((l, i) => {
          const d = isDone(course.id, l.id);
          return (
            <motion.li key={l.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.05 }}>
              <Link to={`/course/${course.id}/${l.id}`} className={`lesson-row ${d ? 'is-done' : ''}`}>
                <span className="lesson-row-num">{d ? <CircleCheckBig size={20} /> : i + 1}</span>
                <span className="lesson-row-icon"><Icon name={l.icon} size={20} /></span>
                <span className="lesson-row-text">
                  <strong>{l.title}</strong>
                  <small>{l.summary}</small>
                </span>
                <span className="lesson-row-min">{l.minutes} min</span>
              </Link>
            </motion.li>
          );
        })}
        <motion.li initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + course.lessons.length * 0.05 }}>
          <Link to={`/course/${course.id}/quiz`} className="lesson-row quiz-row">
            <span className="lesson-row-num"><Trophy size={20} /></span>
            <span className="lesson-row-text">
              <strong>Step {course.step} quiz</strong>
              <small>{course.quiz.length} questions to check what you learned.</small>
            </span>
            <span className="lesson-row-min">{q !== undefined ? `${q}/${course.quiz.length}` : 'Start'}</span>
          </Link>
        </motion.li>
      </ol>

      <nav className="pager">
        {prev ? <Link to={`/course/${prev.id}`} className="pager-link"><ArrowLeft size={18} /><span><small>Step {prev.step}</small>{prev.title}</span></Link> : <span />}
        {next && <Link to={`/course/${next.id}`} className="pager-link right"><span><small>Step {next.step}</small>{next.title}</span><ArrowRight size={18} /></Link>}
      </nav>
    </div>
  );
}
